import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthState, User } from '../../types';
import * as api from '../../services/api';
import axios from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { API_URL } from '../../config/api';
import { getSessionToken, setSessionToken } from '../../services/sessionToken';

// Kiểm tra token trong localStorage
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

interface LoadUserResponse {
  user: User;
}

export const loadUser = createAsyncThunk<LoadUserResponse>(
  'auth/loadUser',
  async (_, { rejectWithValue }) => {
    try {
      const legacy = localStorage.getItem('token');
      if (legacy) {
        setSessionToken(legacy);
        localStorage.removeItem('token');
      }
      const sessionToken = getSessionToken();

      const response = await axios.get<LoadUserResponse>(`${API_URL}/auth/me`, {
        withCredentials: true,
        timeout: 10000,
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : undefined,
      });

      return response.data;
    } catch (error: any) {
      localStorage.removeItem('token');
      return rejectWithValue(error.response?.data || 'Authentication failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async ({ username, email, password }: { username: string; email: string; password: string }) => {
    const response = await api.register({ username, email, password });
    return response;
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }) => {
    const response = await api.login({ email, password });
    localStorage.removeItem('token');
    setSessionToken(response.token);
    return { user: response.user, token: response.token || null };
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await api.logout();
  } catch {
    /* vẫn xóa phiên phía client */
  }
  localStorage.removeItem('token');
  setSessionToken(null);
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Load User
    builder
      .addCase(loadUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Registration failed';
      });

    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Login failed';
      });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    });
  },
});

export const { updateUser, clearAuth } = authSlice.actions;

export default authSlice.reducer; 